<template>
  <div class="evaluation-container max-w-4xl mx-auto p-4" >
    <LoadingOverlay :visible="saving" text="Guardando..." spinnerClass="text-blue-500" textClass="text-green-300" />    
    <h2 class="text-lg font-bold mb-6">{{ traslateType(evaluation.type) }}</h2>
    <hr />
    <div class="w-full" v-if="user.name">
      <h5>Evaluación para : <b> {{ user.name }} {{ user.last_name }} - Cargo: {{ user.organization_chart.profile.name }}</b></h5>
      <hr />
    </div>
    <div class="container-bar w-full">
      <ul class="progressbar w-full m-auto">
        <li
          v-for="(competency, i) in evaluation.competencies"
          :key="i"
          :class="{ active: currentStep > i }"
        >{{ competency.name }}</li>
        <li :class="{ active: currentStep === evaluation.competencies.length + 1 }">Comentario Final</li>
      </ul>
    </div>
    <hr />

    <div
      v-for="(competency, index) in evaluation.competencies"
      :key="competency.id"
      v-show="index === currentStep - 1"
      class="competency-section mb-4"
    >
      <h3 class="text-lg font-semibold text-blue-700">{{ competency.name }}</h3>
      <p>{{ competency.competency_definition }}</p>
      <div
        v-for="(question, number) in competency.questions"
        :key="question.id"
        class="question-section mt-3"
      >
        <p class="text-sm text-gray-800">{{ number + 1 }} - ¿ {{ question.question_text }} ?</p>
        <div class="response-options mt-2 flex flex-wrap gap-2">
          <div
            v-for="option in evaluation.response_options"
            :key="option.id"
            class="option"
          >
            <input
              type="radio"
              :id="'question-' + question.id + '-option-' + option.id"
              :name="'question-' + question.id"
              :value="option.id"
              v-model="responses[question.id]"
              class="hidden"
            />
            <label
              :for="'question-' + question.id + '-option-' + option.id"
              class="response-label"
              :class="{
                'selected': responses[question.id] === option.id,
              }"
            >
              {{ option.description }}
            </label>
          </div>
        </div>
      </div>
      <textarea
        v-model="competencyComments[competency.id]"
        class="w-full mt-4 p-2 border border-gray-300 rounded-md"
        placeholder="Opcional: Agrega un comentario sobre esta competencia..."
      ></textarea>
    </div>

    <div v-if="currentStep === evaluation.competencies.length + 1" class="general-comment-section">
      <h3 class="text-xl font-semibold text-blue-700">Comentario Final</h3>
      <textarea
        v-model="finalComment"
        class="w-full mt-4 p-2 border border-gray-300 rounded-md"
        placeholder="Opcional: Agrega un comentario general sobre la evaluación..."
      ></textarea>
    </div>

    <div class="flex justify-end mt-6" v-if="!saving">
     <button
        @click="nextStep"
        :disabled="!isCurrentStepComplete || saving"
        :class="!isCurrentStepComplete ? 'btn-default text-black' : 'btn-success text-white'"
        class="bg-blue-500 px-4 py-2 rounded"
      >
        {{ currentStep === evaluation.competencies.length + 1 ? 'Finalizar' : 'Siguiente' }}
      </button>
    </div>
  </div>
</template>

<script>
import request from '../../services/request'
import LoadingOverlay from '../GlobalLoad.vue';

import { mapActions } from 'vuex';

export default {
  components: { LoadingOverlay },
  props: {
    evaluation: {
      type: [Object, Array],
      required: true,
    },
    user: {
      type: [Object, Array],
      default: () => []
    },
  },
  data() {
    return {
      responses: {},
      competencyComments: {},
      finalComment: "",
      currentStep: 1,
      saving: false,
      isLoading: false,
      scoreRanges: [
        { score: 1, range: "0% - 30%" },
        { score: 2, range: "31% - 60%" },
        { score: 3, range: "61% - 90%" },
        { score: 4, range: "91% - 100%" },
      ],
    };
  },
  computed: {
    isCurrentStepComplete() {
      if (this.currentStep <= this.evaluation.competencies.length) {
        const currentCompetency = this.evaluation.competencies[this.currentStep - 1];
        return currentCompetency.questions.every(
          (question) => this.responses[question.id]
        );
      } else if (this.currentStep === this.evaluation.competencies.length + 1) {
        return true; // Final comment is optional.
      }
      return false;
    },
  },
  methods: {
    traslateType(type) {
      const types = {
        autoevaluation: "Autoevaluación",
        manager_evaluation: "Evaluación de Jefe",
      };
      return types[type] || "Evaluación General";
    },
    async submitEvaluation() {
      this.saving = true;
        const responsesPayload = Object.entries(this.responses).map(
          ([question_id, answer_id]) => {
            const competency = this.evaluation.competencies.find(competency =>
              competency.questions.some(question => question.id === parseInt(question_id))
            );
            return {
              competency_id: competency.id,
              evaluation_id: this.evaluation.id,
              answer_id: answer_id,
              question_id: question_id,
            };
          }
        );

        const commentsPayload = Object.entries(this.competencyComments).map(
          ([competency_id, comment]) => ({
            competency_id: parseInt(competency_id),
            comment,
          })
        );

      try {
          let service = request;          
          let { data } = await service.post("/evaluations/complete", {
            evaluation_id: this.evaluation.id,
              user_id: this.user.id,
              responses: responsesPayload,
              comments: commentsPayload,
              final_comment: this.finalComment,
          });
          if (data) {
              this.successNotification("Evaluación enviada exitosamente.");
              this.saving = false;              
              this.$router.push({ path: '/evaluations/manager-evaluations', name: 'manager-evaluations' });
          }
        } catch (error) {
          this.saving = false;
        this.errorNotification("Hubo un error al enviar la evaluación. Por favor, inténtelo de nuevo.");
              
        }
    },
    nextStep() {
      if (this.currentStep === this.evaluation.competencies.length + 1) {
        this.submitEvaluation();
      } else {
        this.currentStep++;
        const element = document.querySelector(".container-bar");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    },
    ...mapActions({
			successNotification: 'alert/success',
			errorNotification: 'alert/error',
		}),
  },
};
</script>

<style scoped>
.evaluation-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 16px;
}

.competency-section {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 16px;
}

.question-section {
  margin-top: 16px;
}

.response-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.response-label {
  display: inline-block;
  padding: 8px 4px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  color: #333;
  background-color: #f9f9f9;
}

.response-label.selected {
  background-color: #2f837f;
  color: #ffffff;
  border-color: #2f837f;
}

input[type="radio"].hidden {
  appearance: none;
}

textarea {
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 8px;
  font-size: 1rem;
  width: 100%;
  resize: none;
  transition: border-color 0.2s ease-in-out;
}

textarea:focus {
  border-color: #2f837f;
  outline: none;
}

.container-bar {
  width: 100%;
  position: relative;
  margin-top: 20px;
}

.progressbar {
  counter-reset: step;
  display: flex;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
}

.progressbar li {
  position: relative;
  width: 14.28%;
  text-align: center;
  list-style-type: none;
}

.progressbar li:before {
  content: counter(step);
  counter-increment: step;
  width: 30px;
  height: 30px;
  display: block;
  margin: 0 auto 10px;
  border-radius: 50%;
  line-height: 30px;
  background: #ffffff;
  color: #bebebe;
  text-align: center;
  font-weight: bold;
  border: 2px solid #bebebe;
}

.progressbar li:after {
  content: '';
  position: absolute;
  width: 100%;
  height: 4px;
  background: #bebebe;
  top: 15px;
  left: -50%;
  z-index: -1;
}

.progressbar li:first-child:after {
  content: none;
}

.progressbar li.active:before {
  background: #2f837f;
  color: #ffffff;
  border-color: #2f837f;
}

.progressbar li.active:after {
  background: #2f837f;
}
</style>
